// Qué streams de la flota se le piden al servidor. El servidor filtra por `mdmStatus`; la fase
// (sin confirmar, corte vencido…) la sigue leyendo el navegador con `readFleetState`.
import { LoanDeviceMdmStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceMdmStatusEnum';
import { IMEI_PATTERN } from '@/lib/imei';
import { MDM_STATUS_PRESENTATION, type MdmFleetState } from './logic';
import { ANY_FILTER, type AnyFilter } from './types';

// Un stream por estado del expediente, en el orden del ciclo de vida. Un equipo que cambia de
// estado entre lecturas sale en dos, y `latestByCredit` se queda con su copia más nueva.
export const FLEET_STREAM_STATUSES = [
    LoanDeviceMdmStatusEnum.NOT_ENROLLED,
    LoanDeviceMdmStatusEnum.ENROLLING,
    LoanDeviceMdmStatusEnum.ACTIVE,
    LoanDeviceMdmStatusEnum.LOCKED,
    LoanDeviceMdmStatusEnum.RELEASED,
    LoanDeviceMdmStatusEnum.ARCHIVED,
    LoanDeviceMdmStatusEnum.FAILED,
    LoanDeviceMdmStatusEnum.FAILED_NOT_FOUND,
    LoanDeviceMdmStatusEnum.MANUAL_REVIEW,
] as const;

/** Las fases que `readFleetState` deriva de un ACTIVE o de un LOCKED. */
const PHASE_STATUS: Partial<Record<MdmFleetState, LoanDeviceMdmStatusEnum>> = {
    activeUnconfirmed: LoanDeviceMdmStatusEnum.ACTIVE,
    cutoffPassed: LoanDeviceMdmStatusEnum.ACTIVE,
    cutoffRefuted: LoanDeviceMdmStatusEnum.ACTIVE,
    scheduled: LoanDeviceMdmStatusEnum.LOCKED,
    unconfirmed: LoanDeviceMdmStatusEnum.LOCKED,
    refuted: LoanDeviceMdmStatusEnum.LOCKED,
    locked: LoanDeviceMdmStatusEnum.LOCKED,
};

/** El `mdmStatus` del que sale una opción del filtro «Estado MDM». */
export function fleetStateStatus(state: MdmFleetState): LoanDeviceMdmStatusEnum {
    return PHASE_STATUS[state] ?? (state as LoanDeviceMdmStatusEnum);
}

/** Los streams que pide una opción del filtro: todos sin filtro, uno con él. */
export function fleetStreamStatuses(
    state: MdmFleetState | AnyFilter,
): readonly LoanDeviceMdmStatusEnum[] {
    return state === ANY_FILTER ? FLEET_STREAM_STATUSES : [fleetStateStatus(state)];
}

/** «Bloqueado» · «Bloqueado ni Activo» · «Bloqueado, Activo ni Enrolando». */
function joinLabels(labels: readonly string[]): string {
    if (labels.length <= 1) return labels.join('');
    return `${labels.slice(0, -1).join(', ')} ni ${labels.at(-1) ?? ''}`;
}

/**
 * El aviso de los estados cuyo stream no trajo NADA: no son «más dispositivos» por traer, faltan
 * enteros de la tabla. `null` si no se cayó ninguno.
 */
export function fleetReadFailureNote(statuses: readonly LoanDeviceMdmStatusEnum[]): string | null {
    if (statuses.length === 0) return null;
    const labels = statuses.map((status) => MDM_STATUS_PRESENTATION[status].shortLabel);
    const which =
        labels.length === 1
            ? `en estado ${labels[0] ?? ''}`
            : `en los estados ${joinLabels(labels)}`;
    return `No se pudieron leer los equipos ${which}: faltan enteros de la tabla y los conteos no los incluyen. Vuelve a pedirlos con el botón del pie de la tabla.`;
}

/** El IMEI a buscar en el servidor; `undefined` si el texto no son exactamente 15 dígitos. */
export function imeiSearchOf(text: string): string | undefined {
    const value = text.trim();
    return IMEI_PATTERN.test(value) ? value : undefined;
}
